import React, { useCallback, useEffect, useState } from "react";
import { Check, Copy, KeyRound, Plus, ShieldCheck, UserX, UserCheck, X } from "lucide-react";
import {
  maintenanceApi,
  ZitadelProjectRole,
  ZitadelProvisionedOrg,
  ZitadelUser,
  ZitadelCreateUserResp,
  ZitadelAuthFactor,
} from "../../../api/maintenance";
import "./zitadel-provisioning.scss";
import { useNotice } from "../shared/useNotice";

/** Manage dashboard users of an org that already has a Zitadel tenant. */

function errText(err: any, fallback: string): string {
  const d = err?.response?.data?.detail;
  if (!d) return fallback;
  if (typeof d === "string") return d;
  if (typeof d === "object") {
    const z = d.zitadel?.message || "";
    return [d.message, typeof z === "string" ? z : ""].filter(Boolean).join(" — ");
  }
  return fallback;
}

const isActive = (s: string) => s === "USER_STATE_ACTIVE";

const CopyBtn: React.FC<{ value: string }> = ({ value }) => {
  const [c, setC] = useState(false);
  return (
    <button
      type="button"
      className="maint-btn-small"
      title="Copy"
      onClick={async () => {
        try {
          await navigator.clipboard.writeText(value);
          setC(true);
          setTimeout(() => setC(false), 1500);
        } catch {
          /* noop */
        }
      }}
    >
      {c ? <Check size={14} /> : <Copy size={14} />}
    </button>
  );
};

const ZitadelManageUsers: React.FC = () => {
  const [orgs, setOrgs] = useState<ZitadelProvisionedOrg[]>([]);
  const [roles, setRoles] = useState<ZitadelProjectRole[]>([]);
  const [orgTenantId, setOrgTenantId] = useState("");
  const [users, setUsers] = useState<ZitadelUser[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useNotice("error", "");
  const [message, setMessage] = useNotice("success", "");

  const [showAdd, setShowAdd] = useState(false);
  const [editing, setEditing] = useState<ZitadelUser | null>(null);
  const [securityFor, setSecurityFor] = useState<ZitadelUser | null>(null);

  useEffect(() => {
    maintenanceApi
      .zitadelProvisionedOrgs()
      .then((r) => setOrgs(r.data))
      .catch((e) => setError(errText(e, "Failed to load provisioned orgs")));
    maintenanceApi
      .zitadelGetProjectRoles()
      .then((r) => setRoles(r.data))
      .catch(() => {});
  }, []);

  const loadUsers = useCallback(async (tenantId: string) => {
    if (!tenantId) {
      setUsers([]);
      return;
    }
    setLoading(true);
    setError("");
    try {
      const r = await maintenanceApi.zitadelListUsers(tenantId);
      setUsers(r.data);
    } catch (e) {
      setError(errText(e, "Failed to load users"));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadUsers(orgTenantId);
  }, [orgTenantId, loadUsers]);

  const toggleActive = async (u: ZitadelUser) => {
    setError("");
    setMessage("");
    try {
      if (isActive(u.state)) {
        if (!window.confirm(`Deactivate ${u.username}? They will not be able to log in.`))
          return;
        await maintenanceApi.zitadelDeactivateUser(orgTenantId, u.user_id);
        setMessage(`${u.username} deactivated`);
      } else {
        await maintenanceApi.zitadelReactivateUser(orgTenantId, u.user_id);
        setMessage(`${u.username} reactivated`);
      }
      loadUsers(orgTenantId);
    } catch (e) {
      setError(errText(e, "Action failed"));
    }
  };

  return (
    <div className="maint-stations">
      <div className="maint-toolbar" style={{ gap: 12 }}>
        <label style={{ flex: 1 }}>
          <span style={{ fontSize: 13, fontWeight: 600 }}>Organization</span>
          <select
            value={orgTenantId}
            onChange={(e) => {
              setOrgTenantId(e.target.value);
              setMessage("");
              setError("");
            }}
          >
            <option value="">Select a provisioned org...</option>
            {orgs.map((o) => (
              <option key={o.org_custom_id} value={o.zitadel_tenant_id}>
                {o.name}
              </option>
            ))}
          </select>
        </label>
        <button
          className="maint-btn-primary"
          disabled={!orgTenantId}
          onClick={() => setShowAdd(true)}
        >
          <Plus size={16} /> Add User
        </button>
      </div>

      {message && <p className="maint-success">{message}</p>}
      {error && <p className="maint-form-error">{error}</p>}

      {showAdd && (
        <AddUserModal
          orgTenantId={orgTenantId}
          roles={roles}
          loginUrl={
            orgs.find((o) => o.zitadel_tenant_id === orgTenantId)
              ?.platform_url || ""
          }
          orgName={
            orgs.find((o) => o.zitadel_tenant_id === orgTenantId)?.name || ""
          }
          onClose={() => setShowAdd(false)}
          onDone={() => {
            setShowAdd(false);
            setMessage("User created");
            loadUsers(orgTenantId);
          }}
        />
      )}

      {editing && (
        <EditRolesModal
          orgTenantId={orgTenantId}
          user={editing}
          roles={roles}
          onClose={() => setEditing(null)}
          onDone={() => {
            setEditing(null);
            setMessage("Roles updated");
            loadUsers(orgTenantId);
          }}
        />
      )}

      {securityFor && (
        <SecurityModal
          orgTenantId={orgTenantId}
          user={securityFor}
          onClose={() => setSecurityFor(null)}
          onChanged={(msg) => setMessage(msg)}
        />
      )}

      {!orgTenantId ? (
        <p className="maint-empty">Select an organization to manage its users.</p>
      ) : loading ? (
        <p className="maint-loading">Loading...</p>
      ) : users.length === 0 ? (
        <p className="maint-empty">No users in this org yet.</p>
      ) : (
        <table className="maint-table">
          <thead>
            <tr>
              <th>Username</th>
              <th>Email</th>
              <th>State</th>
              <th>Roles</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.user_id}>
                <td>{u.username}</td>
                <td>{u.email || "—"}</td>
                <td>
                  <span
                    className={
                      isActive(u.state)
                        ? "maint-status-online"
                        : "maint-status-offline"
                    }
                  >
                    {isActive(u.state) ? "Active" : "Inactive"}
                  </span>
                </td>
                <td>{u.role_keys.join(", ") || "—"}</td>
                <td>
                  <div style={{ display: "flex", gap: 4 }}>
                    <button
                      className="maint-btn-small"
                      title="Edit roles"
                      onClick={() => setEditing(u)}
                    >
                      <ShieldCheck size={14} />
                    </button>
                    <button
                      className="maint-btn-small"
                      title="Sign-in security (2FA reset, password reset)"
                      onClick={() => setSecurityFor(u)}
                    >
                      <KeyRound size={14} />
                    </button>
                    <button
                      className="maint-btn-small"
                      title={isActive(u.state) ? "Deactivate" : "Reactivate"}
                      onClick={() => toggleActive(u)}
                    >
                      {isActive(u.state) ? (
                        <UserX size={14} />
                      ) : (
                        <UserCheck size={14} />
                      )}
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
};

// ---------------------------------------------------------------- add user

const AddUserModal: React.FC<{
  orgTenantId: string;
  roles: ZitadelProjectRole[];
  loginUrl: string;
  orgName: string;
  onClose: () => void;
  onDone: () => void;
}> = ({ orgTenantId, roles, loginUrl, orgName, onClose, onDone }) => {
  const [f, setF] = useState({
    email: "",
    first_name: "",
    last_name: "",
    username: "",
    initial_password: "",
  });
  const [picked, setPicked] = useState<string[]>(
    roles.find((r) => r.key === "org-admin") ? ["org-admin"] : []
  );
  const [sendEmail, setSendEmail] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useNotice("error", "");
  const [created, setCreated] = useState<ZitadelCreateUserResp | null>(null);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!f.email || !f.first_name || !f.last_name)
      return setError("Email, first and last name are required");
    if (picked.length === 0) return setError("Pick at least one role");
    setBusy(true);
    setError("");
    try {
      const r = await maintenanceApi.zitadelCreateUser(orgTenantId, {
        email: f.email.trim(),
        first_name: f.first_name.trim(),
        last_name: f.last_name.trim(),
        username: f.username.trim() || undefined,
        initial_password: f.initial_password.trim() || undefined,
        role_keys: picked,
        send_email: sendEmail,
        login_url: loginUrl || undefined,
        org_name: orgName || undefined,
      });
      setCreated(r.data);
    } catch (err) {
      setError(errText(err, "Failed to create user"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="maint-form-overlay">
      <div className="maint-form-card maint-form-card--wide">
        <div className="maint-form-header">
          <h3>Add Dashboard User</h3>
          <button onClick={onClose} className="maint-btn-icon">
            <X size={18} />
          </button>
        </div>

        {created ? (
          <div style={{ padding: 4 }}>
            <p className="maint-success">
              Created <b>{created.username}</b>. The user must change this
              password on first login.
            </p>
            <div className="ztl-secret" style={{ margin: "12px 0 6px" }}>
              <code>{created.initial_password}</code>
              <CopyBtn value={created.initial_password} />
            </div>
            <p
              style={{
                fontSize: 12,
                margin: "0 0 12px",
                color: created.email_sent ? "#2e7d32" : "#b26a00",
              }}
            >
              {created.email_sent
                ? `✓ Credentials emailed to ${created.email}`
                : `⚠ Email not sent${
                    created.email_error ? ` (${created.email_error})` : ""
                  } — share the password manually`}
            </p>
            <div className="maint-form-actions">
              <button className="maint-btn-primary" onClick={onDone}>
                Done
              </button>
            </div>
          </div>
        ) : (
          <form onSubmit={submit}>
            <div className="maint-form-row">
              <label className="ztl-field" style={{ flex: 1 }}>
                <span>Email *</span>
                <input
                  value={f.email}
                  onChange={(e) => setF({ ...f, email: e.target.value })}
                />
              </label>
              <label className="ztl-field" style={{ flex: 1 }}>
                <span>Username</span>
                <input
                  value={f.username}
                  onChange={(e) => setF({ ...f, username: e.target.value })}
                  placeholder="defaults to email local part"
                />
              </label>
            </div>
            <div className="maint-form-row">
              <label className="ztl-field" style={{ flex: 1 }}>
                <span>First name *</span>
                <input
                  value={f.first_name}
                  onChange={(e) => setF({ ...f, first_name: e.target.value })}
                />
              </label>
              <label className="ztl-field" style={{ flex: 1 }}>
                <span>Last name *</span>
                <input
                  value={f.last_name}
                  onChange={(e) => setF({ ...f, last_name: e.target.value })}
                />
              </label>
            </div>
            <label className="ztl-field">
              <span>Initial password (optional — generated if blank)</span>
              <input
                value={f.initial_password}
                onChange={(e) =>
                  setF({ ...f, initial_password: e.target.value })
                }
              />
            </label>
            <span style={{ fontSize: 13, fontWeight: 600 }}>Roles</span>
            <div className="ztl-roles">
              {roles.map((r) => (
                <label key={r.key}>
                  <input
                    type="checkbox"
                    checked={picked.includes(r.key)}
                    onChange={() =>
                      setPicked(
                        picked.includes(r.key)
                          ? picked.filter((k) => k !== r.key)
                          : [...picked, r.key]
                      )
                    }
                  />
                  {r.display_name || r.key}
                </label>
              ))}
            </div>
            <label
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                fontSize: 13,
                margin: "12px 0 0",
              }}
            >
              <input
                type="checkbox"
                checked={sendEmail}
                onChange={(e) => setSendEmail(e.target.checked)}
              />
              Email credentials to the user
              {sendEmail && !loginUrl && (
                <span style={{ color: "#b26a00", fontSize: 12 }}>
                  (no platform URL set for this org)
                </span>
              )}
            </label>
            {error && <p className="maint-form-error">{error}</p>}
            <div className="maint-form-actions">
              <button
                type="button"
                onClick={onClose}
                className="maint-btn-secondary"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={busy}
                className="maint-btn-primary"
              >
                {busy ? "Creating..." : "Create User"}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

// ---------------------------------------------------------------- edit roles

const EditRolesModal: React.FC<{
  orgTenantId: string;
  user: ZitadelUser;
  roles: ZitadelProjectRole[];
  onClose: () => void;
  onDone: () => void;
}> = ({ orgTenantId, user, roles, onClose, onDone }) => {
  const [picked, setPicked] = useState<string[]>(user.role_keys);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useNotice("error", "");

  const save = async () => {
    if (picked.length === 0) return setError("Pick at least one role");
    setBusy(true);
    setError("");
    try {
      await maintenanceApi.zitadelUpdateUserRoles(orgTenantId, user.user_id, {
        role_keys: picked,
      });
      onDone();
    } catch (e) {
      setError(errText(e, "Failed to update roles"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="maint-form-overlay">
      <div className="maint-form-card">
        <div className="maint-form-header">
          <h3>Roles — {user.username}</h3>
          <button onClick={onClose} className="maint-btn-icon">
            <X size={18} />
          </button>
        </div>
        <div className="ztl-roles" style={{ marginBottom: 14 }}>
          {roles.map((r) => (
            <label key={r.key}>
              <input
                type="checkbox"
                checked={picked.includes(r.key)}
                onChange={() =>
                  setPicked(
                    picked.includes(r.key)
                      ? picked.filter((k) => k !== r.key)
                      : [...picked, r.key]
                  )
                }
              />
              {r.display_name || r.key}
            </label>
          ))}
        </div>
        {error && <p className="maint-form-error">{error}</p>}
        <div className="maint-form-actions">
          <button onClick={onClose} className="maint-btn-secondary">
            Cancel
          </button>
          <button
            onClick={save}
            disabled={busy}
            className="maint-btn-primary"
          >
            {busy ? "Saving..." : "Save Roles"}
          </button>
        </div>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------- security

const FACTOR_LABELS: Record<ZitadelAuthFactor["type"], string> = {
  totp: "Authenticator app",
  otp_sms: "SMS code",
  otp_email: "Email code",
  u2f: "Security key",
};

/**
 * Account recovery for one user: see which second factors they have, remove
 * one when they are locked out (lost phone / authenticator), or send them a
 * password-reset email. Enrolment itself is self-service (CMS "My Account").
 */
const SecurityModal: React.FC<{
  orgTenantId: string;
  user: ZitadelUser;
  onClose: () => void;
  onChanged: (msg: string) => void;
}> = ({ orgTenantId, user, onClose, onChanged }) => {
  const [factors, setFactors] = useState<ZitadelAuthFactor[] | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useNotice("error", "");
  const [note, setNote] = useState("");

  const load = useCallback(async () => {
    setError("");
    try {
      const r = await maintenanceApi.zitadelUserAuthFactors(orgTenantId, user.user_id);
      setFactors(r.data);
    } catch (e) {
      setError(errText(e, "Failed to load second factors"));
      setFactors([]);
    }
  }, [orgTenantId, user.user_id]);

  useEffect(() => {
    load();
  }, [load]);

  const remove = async (f: ZitadelAuthFactor) => {
    const label = f.type === "u2f" ? `security key "${f.name || f.id}"` : FACTOR_LABELS[f.type];
    if (
      !window.confirm(
        `Remove ${label} from ${user.username}? They will sign in with just their password until they set it up again.`
      )
    )
      return;
    const key = `${f.type}:${f.id || ""}`;
    setBusy(key);
    setError("");
    try {
      await maintenanceApi.zitadelRemoveUserAuthFactor(orgTenantId, user.user_id, f.type, f.id);
      setNote(`${FACTOR_LABELS[f.type]} removed`);
      onChanged(`${FACTOR_LABELS[f.type]} removed for ${user.username}`);
      await load();
    } catch (e) {
      setError(errText(e, "Failed to remove factor"));
    } finally {
      setBusy(null);
    }
  };

  const resetPassword = async () => {
    if (!window.confirm(`Email a password-reset link to ${user.email || user.username}?`)) return;
    setBusy("pwreset");
    setError("");
    try {
      await maintenanceApi.zitadelSendPasswordReset(orgTenantId, user.user_id);
      setNote(`Password-reset email sent to ${user.email || user.username}`);
      onChanged(`Password-reset email sent to ${user.username}`);
    } catch (e) {
      setError(errText(e, "Failed to send password reset"));
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="maint-form-overlay">
      <div className="maint-form-card">
        <div className="maint-form-header">
          <h3>Sign-in security — {user.username}</h3>
          <button onClick={onClose} className="maint-btn-icon">
            <X size={18} />
          </button>
        </div>

        <p className="ztl-summary" style={{ color: "#888", marginBottom: 12 }}>
          Users enrol 2FA themselves under <em>My Account</em>. Remove a factor
          here only when someone is locked out.
        </p>

        {factors === null ? (
          <p className="maint-loading">Loading...</p>
        ) : factors.length === 0 ? (
          <p className="maint-empty">No second factors enrolled.</p>
        ) : (
          <table className="maint-table" style={{ marginBottom: 14 }}>
            <thead>
              <tr>
                <th>Method</th>
                <th>State</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {factors.map((f) => {
                const key = `${f.type}:${f.id || ""}`;
                return (
                  <tr key={key}>
                    <td>
                      {FACTOR_LABELS[f.type]}
                      {f.type === "u2f" && f.name ? ` — ${f.name}` : ""}
                    </td>
                    <td>
                      <span className={f.ready ? "maint-status-online" : "maint-status-offline"}>
                        {f.ready ? "Ready" : "Not verified"}
                      </span>
                    </td>
                    <td style={{ textAlign: "right" }}>
                      <button
                        className="maint-btn-small"
                        disabled={busy === key}
                        onClick={() => remove(f)}
                      >
                        {busy === key ? "Removing..." : "Remove"}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}

        {note && <p className="maint-success">{note}</p>}
        {error && <p className="maint-form-error">{error}</p>}

        <div className="maint-form-actions">
          <button
            className="maint-btn-secondary"
            disabled={busy === "pwreset" || !user.email}
            title={user.email ? "" : "User has no email on file"}
            onClick={resetPassword}
          >
            {busy === "pwreset" ? "Sending..." : "Email password reset"}
          </button>
          <button onClick={onClose} className="maint-btn-primary">
            Done
          </button>
        </div>
      </div>
    </div>
  );
};

export default ZitadelManageUsers;
